<script lang="ts">
  import { cn } from "@/lib/utils";

  export let totalCarbs: number; // grams
  export let totalFat: number;   // grams
  export let revealed: boolean = false;

  const tidy = (n: number) => new Intl.NumberFormat().format(Math.round(n ?? 0));
  const calories = (totalCarbs ?? 0) * 4 + (totalFat ?? 0) * 9;
  const pizzaSlices = Math.floor(calories / 285);
  const fritters = Math.floor((totalCarbs ?? 0) / 68);

  // Macro donut (carbs vs fat in kcals)
  const carbCalories = (totalCarbs ?? 0) * 4;
  const fatCalories = (totalFat ?? 0) * 9;
  const totalKcals = carbCalories + fatCalories;
  const carbPct = totalKcals > 0 ? carbCalories / totalKcals : 0;
  const fatPct = totalKcals > 0 ? fatCalories / totalKcals : 0;
  const carbDeg = Math.round(carbPct * 360);
</script>

<div class='w-full rounded-2xl p-[1px] nutrition-card-shell'
  style='box-shadow: 0 10px 28px rgba(0,0,0,0.35), inset 0 0 8px rgba(255,255,255,0.05);'>
  <div class='rounded-2xl nutrition-card-inner text-off-white'>
    <div class='w-full grid grid-cols-12 gap-5 p-5 overflow-hidden'>

      <!-- Macro donut: carbs vs fat calories -->
      <div class='col-span-12 flex items-center justify-center'>
        <div class={cn('donut-wrap', revealed ? 'donut-pop' : '')}>
          <div class='donut-ring'
            style={`background: url("/assets/images/sprinkles.png"), conic-gradient(#03d5e7 0deg ${carbDeg}deg, #fea2c5 ${carbDeg}deg 360deg); box-shadow: 0 0 18px rgba(255,255,255,0.06), 0 0 22px rgba(255,209,102,0.18); background-size: contain;`}
          ></div>
          <div class='donut-center'>
            <div class='w-[100px] h-[100px] bg-[rgb(8,9,10)] rounded-full'/>
          </div>
          <div class='donut-center'>
            <i class='fa-light fa-fire' style={`color:#FFD166;`}></i>
            <div class='value'>{tidy(calories)}</div>
            <div class='label'>kcal</div>
          </div>
        </div>
      </div>

      <!-- Legend + macros -->
      <div class='col-span-12 flex flex-col gap-3'>
        <div class={cn('legend', revealed ? 'legend-in' : '')}>
          <div class='legend-item text-[#03d5e7]'>
            <span class='dot carbs'></span>
            <span>Carbs</span>
            <span class='sep'>•</span>
            <span class='grams'>{tidy(totalCarbs)} g</span>
            <span class='sep'>•</span>
            <span class='kcals'>{tidy(carbCalories)} kcal</span>
          </div>
          <div class='legend-item text-[#fea2c5]'>
            <span class='dot fat'></span>
            <span>Fat</span>
            <span class='sep'>•</span>
            <span class='grams'>{tidy(totalFat)} g</span>
            <span class='sep'>•</span>
            <span class='kcals'>{tidy(fatCalories)} kcal</span>
          </div>
        </div>

        <!-- Dessert badges -->
        <div class='grid grid-cols-2 gap-3'>
          <div class={cn('badge flex flex-col gap-0 text-[#fea2c5]', revealed ? 'badge-in-1' : '')}>
            <div class='flex flex-row gap-1 items-center'>
              <i class='fa-light fa-burger-fries text-[#fea2c5]'></i>
              <div class='flex-1 text-xl'>{Math.floor((totalFat / 454))}</div>
            </div>
            <div class='text-xs font-light text-wrap'>Pounds you didn't gain</div>
          </div>
          <div class={cn('badge flex flex-col gap-0 text-[#03d5e7]', revealed ? 'badge-in-2' : '')}>
            <div class='flex flex-row gap-1 items-center'>
              <i class='fa-light fa-donut text-[#03d5e7]'></i>
              <div class='flex-1 text-xl'>{Math.floor(fritters)}</div>
            </div>
            <div class='text-xs font-light text-wrap'>Apple fritters</div>
          </div>
        </div>

        <!-- CTA note -->
        <div class={cn('cta flex flex-row items-center justify-center gap-2', revealed ? 'cta-in' : '')}>
          <i class='fa-light fa-pizza-slice' style={`color:#FFD166;`}></i>
          <div class='text-xl font-bold'>{Math.floor(pizzaSlices)}</div>
          <span class='text-sm'>Pizza Slices</span>
        </div>
      </div>
    </div>
  </div>
</div>

<style>
  /* Unique card chrome */
  .nutrition-card-shell {
    background: linear-gradient(5deg, rgba(18,20,22,1) 60%, rgba(40,44,48,0.18) 85%, rgba(255,209,102,0.10) 100%);
  }
  .nutrition-card-inner {
    background:
      radial-gradient(circle at 80% 10%, rgba(255,255,255,0.06), transparent 40%),
      radial-gradient(circle at 80% 90%, rgba(255,255,255,0.04), transparent 40%),
      linear-gradient(180deg, rgba(18,20,22,0.95), rgba(8,9,10,0.98));
    border-radius: 1rem;
  }

  /* Donut chart */
  .donut-wrap { position: relative; width: 180px; height: 180px; opacity: 0; }
  .donut-ring { width: 180px; height: 180px; border-radius: 999px; }
  .donut-center { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; }
  .donut-center .value { font-size: 1.4rem; font-weight: 800; }
  .donut-center .label { font-size: 0.85rem; opacity: 0.85; }
  .donut-pop { animation: donut-pop 520ms ease-out both; }
  @keyframes donut-pop {
    0% { opacity: 0; transform: scale(0.92) rotate(-6deg); }
    70%{ opacity: 1; transform: scale(1.02) rotate(0deg); }
    100%{ opacity: 1; transform: scale(1) rotate(0deg); }
  }

  /* Legend */
  .legend { display: flex; flex-direction: column; gap: 8px; opacity: 0; transform: translateY(8px); }
  .legend-in { animation: legend-in 420ms 360ms ease-out forwards; }
  .legend-item { display: flex; align-items: baseline; gap: 8px; font-size: 0.9rem; }
  .dot { width: 10px; height: 10px; border-radius: 999px; box-shadow: 0 0 8px rgba(255,255,255,0.12); }
  .dot.carbs { background: #03d5e7; }
  .dot.fat { background: #fea2c5; }
  .sep { opacity: 0.5; }
  .grams, .kcals { font-weight: 700; }
  @keyframes legend-in { 0% { opacity: 0; transform: translateY(8px); } 100%{ opacity: 1; transform: translateY(0); } }

  /* Badges */
  .badge { border-radius: 12px; border: 1px solid rgba(255,255,255,0.10); background: rgba(255,255,255,0.05); padding: 10px 12px; display: flex; align-items: center; gap: 10px; opacity: 0; transform: translateY(12px); }
  .badge i { color: #cfd8dc; text-shadow: 0 0 8px rgba(255,255,255,0.08); }
  .badge .metric .value { font-size: 1.25rem; font-weight: 800; }
  .badge .metric .label { font-size: 0.8rem; opacity: 0.8; }
  .badge-in-1 { animation: badge-in 420ms 200ms ease-out forwards; }
  .badge-in-2 { animation: badge-in 420ms 280ms ease-out forwards; }
  @keyframes badge-in { 0% { opacity: 0; transform: translateY(12px); } 100%{ opacity: 1; transform: translateY(0); } }

  /* CTA note */
  .cta { display: flex; align-items: center; gap: 8px; border: 1px solid rgba(255,255,255,0.10); background: rgba(255,255,255,0.05); border-radius: 12px; padding: 10px; opacity: 0; transform: translateY(10px); }
  .cta-in { animation: cta-in 420ms 420ms ease-out forwards; }
  @keyframes cta-in { 0% { opacity: 0; transform: translateY(10px); } 100%{ opacity: 1; transform: translateY(0); } }
</style>
